import Icon1 from "@assets/icons/Icon";
import CheckBoxInput from "../selection/CheckBoxInput";
import { colors } from "sexy";
import { useState } from "react";
import { Timestamp } from "firebase/firestore";
import { ControlPoint, Project, ProjectHistoryEvent } from "types";
import { getFlutterTime } from "@services/timeHelpers";
import { getStateIcon, StepModal } from "@components/modals/StepModal";
import { CustomImage } from "@components/ui/images/CustomImage";
import SearchField from "@components/ui/textfields/SearchField";
import { getVisibleThemesFromProject } from "@helpers/stepHelpers";
import { handleSendFastEmailClient } from "@helpers/emailHelpers";
import { useDataStore } from "@store/user_org";
import { useProjectStore } from "@store/projectStore";
import { projectsApi } from "@services/api";
import { showNotification } from "context/notification.service";

export const StepsList = ({
  label,
  project,
  setProject,
  userData,
  show_photos = false,
  readOnly = false,
  isGuest = false,
}: {
  label: string;
  project: Project;
  setProject: (project: Project) => void;
  userData?: {
    id?: string;
    infos?: {
      display_name?: string;
    };
    customization?: {
      photo_url?: string;
    };
  };
  show_photos?: boolean;
  readOnly?: boolean;
  isGuest?: boolean;
}) => {
  const storeOrgData = useDataStore((state) => state.orgData);
  const storeUserData = useDataStore((state) => state.userData);
  const addSentEmail = useProjectStore((state) => state.addSentEmail);
  const themes = isGuest
    ? getVisibleThemesFromProject(project, userData as any)
    : project.themes || [];

  // Track which steps are expanded
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(() => {
    const initialExpanded = new Set<string>();
    themes.forEach((theme) => {
      theme.steps.forEach((step) => {
        const allCompleted = step.controlpoints.every(
          (cp) => cp.state === "completed" || cp.state === "not_applicable",
        );
        if (!allCompleted) {
          initialExpanded.add(`${theme.label}-${step.label}`);
        }
      });
    });
    return initialExpanded;
  });

  const [search, setSearch] = useState("");

  const [showStepModal, setShowStepModal] = useState<{
    theme_name: string;
    step_name: string;
    controlpoint_id: string;
  } | null>(null);

  const activeModalControlPoint = showStepModal
    ? themes
        .find((t) => t.label === showStepModal.theme_name)
        ?.steps.find((s) => s.label === showStepModal.step_name)
        ?.controlpoints.find((cp) => cp.id === showStepModal.controlpoint_id)
    : null;

  const toggleStepExpansion = (themeValue: string, stepValue: string) => {
    const key = `${themeValue}-${stepValue}`;
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleUpdateHistoryEvents = async (newControlPoint: ControlPoint) => {
    try {
      // Backend now handles state update and history event management
      const updatedProject = await projectsApi.updateControlpointState(
        project.id,
        newControlPoint.id,
        newControlPoint.state,
      );

      setProject(updatedProject);
    } catch (error) {
      console.error("Error updating controlpoint state:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la mise à jour de l'étape",
      });
    }
  };

  const handleToggleControlPoint = (
    themeValue: string,
    stepValue: string,
    controlPoint: ControlPoint,
  ) => {
    const updatedControlPoint: ControlPoint = { ...controlPoint };
    const stateOrder: Array<ControlPoint["state"]> = [
      "default",
      "completed",
      "missed",
      "not_applicable",
    ];
    const currentIndex = stateOrder.indexOf(controlPoint.state);
    const nextState = stateOrder[
      (currentIndex + 1) % stateOrder.length
    ] as ControlPoint["state"];
    updatedControlPoint.state = nextState;

    handleUpdateHistoryEvents(updatedControlPoint);
  };

  const handleSave = (updatedControlPoint: ControlPoint) => {
    handleUpdateHistoryEvents(updatedControlPoint);
  };

  const normalizedSearch = search.trim().toLowerCase();

  const filteredThemes = themes
    .map((theme) => ({
      ...theme,
      steps: theme.steps
        .map((step) => ({
          ...step,
          controlpoints: step.controlpoints.filter((cp) => {
            if (isGuest && cp.hidden) return false;
            if (show_photos && !(cp.photos_id && cp.photos_id.length > 0))
              return false;
            if (
              normalizedSearch &&
              !cp.label.toLowerCase().includes(normalizedSearch)
            )
              return false;
            return true;
          }),
        }))
        .filter((step) => step.controlpoints.length > 0),
    }))
    .filter((theme) => theme.steps.length > 0);

  const totalControlPoints = themes.reduce(
    (acc, theme) =>
      acc +
      theme.steps.reduce((acc2, step) => acc2 + step.controlpoints.length, 0),
    0,
  );

  const completedControlPoints = themes.reduce(
    (acc, theme) =>
      acc +
      theme.steps.reduce(
        (acc2, step) =>
          acc2 +
          step.controlpoints.filter((cp) => cp.state === "completed").length,
        0,
      ),
    0,
  );

  return (
    <div className="flex flex-col w-full min-w-200 max-w-500 rounded-xl border sm:text-sm">
      {showStepModal != null && activeModalControlPoint != null && (
        <StepModal
          onClose={() => setShowStepModal(null)}
          handleSave={handleSave}
          theme_name={showStepModal.theme_name}
          step_name={showStepModal.step_name}
          controlpoint={activeModalControlPoint}
          activeProject={project}
          setActiveProject={setProject}
          userData={userData}
        />
      )}

      {/* Header */}
      <div className=" top-0 border-b pb-4 p-2 z-10 w-400">
        <div className="text-neutral-400 text-h3 px-2 py-2">{label}</div>
        <div className="flex flex-row gap-2 px-2 items-center">
          <div className="text-neutral-400 text-sm">
            ({completedControlPoints}/{totalControlPoints})
          </div>
        </div>
        <div className="px-2 pt-2">
          <SearchField
            value={search}
            onChange={(value) => setSearch(value)}
            placeholder="Rechercher un point de contrôle..."
          />
        </div>
      </div>

      <div className="p-2 ">
        {/* Themes */}
        {filteredThemes.map((theme) => (
          <div key={theme.label} className="mb-4">
            {/* Theme Header */}
            <div className="mb-2">
              <span className="text-h2 font-semibold text-neutral-800">
                {theme.label}
              </span>
            </div>

            {/* Steps */}
            {theme.steps.map((step) => {
              const stepCompletedCount = step.controlpoints.filter(
                (cp) => cp.state === "completed",
              ).length;
              const stepAllCompleted =
                stepCompletedCount === step.controlpoints.length &&
                step.controlpoints.length > 0;

              const isExpanded = expandedSteps.has(
                `${theme.label}-${step.label}`,
              );

              return (
                <div key={step.label} className="mb-1">
                  {/* Step Header */}
                  <div
                    className="flex items-center shadow-md gap-5 px-3.5 py-2.5 cursor-pointer hover:bg-neutral-50 rounded-xl bg-white"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleStepExpansion(theme.label, step.label);
                    }}
                  >
                    <span className="block text-h3 font-medium flex-1 text-neutral-500">
                      {step.label}{" "}
                      <span
                        className={`text-h4 ${
                          stepCompletedCount === step.controlpoints.length
                            ? "text-emerald-400"
                            : "text-neutral-400"
                        }`}
                      >
                        ({stepCompletedCount}/{step.controlpoints.length})
                      </span>
                    </span>
                    <svg
                      className={`w-3.5 h-3.5 text-neutral-400 transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>

                  {/* Control Points */}
                  {isExpanded && (
                    <div className="ml-2.5 mt-2.5 mb-5">
                      {step.controlpoints.map((controlPoint) => (
                        <div
                          key={controlPoint.id}
                          className={`select-none relative rounded-lg py-0.5 mb-0.5 ${readOnly ? "cursor-default" : "cursor-pointer"}`}
                          onClick={(e) => {
                            if (readOnly) return;
                            e.preventDefault();
                            e.stopPropagation();
                            setShowStepModal({
                              theme_name: theme.label,
                              step_name: step.label,
                              controlpoint_id: controlPoint.id,
                            });
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              onClick={(e) => {
                                if (readOnly) return;
                                e.stopPropagation();
                                if (controlPoint.associated_email_template_id) {
                                  // Must go through modal to validate — avoids accidental email sends
                                  setShowStepModal({
                                    theme_name: theme.label,
                                    step_name: step.label,
                                    controlpoint_id: controlPoint.id,
                                  });
                                } else {
                                  handleToggleControlPoint(
                                    theme.label,
                                    step.label,
                                    controlPoint,
                                  );
                                }
                              }}
                            >
                              {getStateIcon(controlPoint.state)}
                            </div>
                            <span
                              className={`flex items-center gap-1 text-h3 flex-1 ${
                                controlPoint.state === "completed"
                                  ? "line-through text-neutral-400"
                                  : controlPoint.state === "missed"
                                    ? "text-neutral-700"
                                    : "text-neutral-400"
                              }`}
                            >
                              {controlPoint.label}
                              {!controlPoint.hidden && (
                                <div className="ml-2 flex flex-row items-center">
                                  <Icon1 name="eye" size={14} color="grey" />
                                </div>
                              )}
                            </span>
                          </div>

                          {show_photos && controlPoint.photos_id && (
                            <div className="flex flex-wrap mb-2 ">
                              {project.photos
                                .filter(
                                  (photo) =>
                                    controlPoint.photos_id?.includes(
                                      photo.id,
                                    ) && (isGuest ? !photo.hidden : true),
                                )
                                .map((photo, index) => (
                                  <CustomImage
                                    key={index}
                                    source={{ uri: photo.file.url }}
                                    className="w-24 h-24 rounded-lg mt-2 mr-2"
                                    hidden={photo.hidden}
                                    alt={`Photo ${index + 1}`}
                                  />
                                ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {filteredThemes.length === 0 && (
          <div className="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-400">
            Aucun thème disponible
          </div>
        )}
      </div>
    </div>
  );
};
